<template>
  <div
    class="calendar-scroll-footer"
    data-component="calendar-scroll-footer"
  >
    <div class="calendar-scroll-footer-top">
      <div class="calendar-scroll-footer-left">
        <div class="price-range">
          <span class="price-range-title">
            {{ numberOfNights }} {{ tc('booking.startingAt') }}
          </span>
          <div
            role="tablist"
            class="toggle-group"
          >
            <button
              v-for="range in priceRanges"
              :id="range + '-range'"
              :key="range"
              role="tab"
              :aria-selected="range === priceRange"
              :aria-label="getRangeAriaLabel(range)"
              :tabindex="range === priceRange ? '0' : '-1'"
              :class="[
                { selected: range === priceRange }
              ]"
              @click="priceRange = range"
              @focus="bindKeyboardEvent"
              @blur="unbindKeyboardEvent"
            >
              {{ tc(`booking.${range}`) }}
            </button>
          </div>
        </div>
        <div class="price-section">
          <span
            class="price-value"
            :style="[
              { opacity: haveClosedDaySelected(localQuery) ? 0.4 : undefined }
            ]"
          >
            {{ getDisplayPrice({price: finalPrice, fromCurrency: hotelCurrency, toCurrency: orSelected(hotelCurrency), withIsoCode: showIsoCode}) }}
            <div class="currency-selector-wrapper">
              <currency-switcher
                :base-currency="hotelCurrency"
                :show-back-btn="true"
                @close="$emit('currency-switcher-closed')"
              >
                <button
                  slot-scope="{ activate }"
                  aria-label="Select Currency"
                  class="currency-selector-button"
                  @click="activate"
                  @keydown.space.prevent="activate"
                >
                  <i
                    aria-hidden="true"
                    class="icon-arrow-up-and-down currency-selector-icon"
                  />
                </button>
              </currency-switcher>
            </div>
          </span>
          <span
            class="price-label"
            aria-describedby="taxes-details"
          >
            {{ finalPriceLabel }}
          </span>
          <button
            class="special-code-add"
            :class="{'special-code-add_selected': havePromoValues}"
            @click="havePromoValues ? removeEspecialCode() : $emit('open-special-code')"
          >
            <template v-if="havePromoValues">
              {{ promoValue }}
              <div class="icon-close" />
            </template>
            <template v-else>
              <div class="icon-plus" />
              <span class="code-text">{{ tc('booking.add') }} {{ tc('booking.specialCode') }}</span>
            </template>
          </button>
        </div>
      </div>
      <div class="calendar-scroll-footer-right">
        <div class="calendar-scroll-footer-right-cta">
          <button
            :disabled="isDisabled"
            :class="[{ loading: isLoading }]"
            aria-describedby="taxes-details"
            @click="submit"
          >
            {{ tc('booking.confirm') }}
            <div
              class="sr-only"
              role="status"
            >
              {{ isLoading ? tc('accessibility.loadingButtonStatus') : '' }}
            </div>
          </button>
          <span
            id="taxes-details"
            class="taxes-details"
          >
            {{ tc('booking.taxesDetails') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { KEY_CODE_MAP } from '@utils/keyboard-helpers'
  import currencySwitcher from '../../../../currency-switcher/currency-switcher.vue'

  export default {
    name: 'CalendarFooter',
    components: { currencySwitcher },
    inject: ['localQuery'],
    props: {
      runRouteValidation: {
        type: Function,
        required: true
      },
    },
    data: () => ({
      isLoading: false,
      priceRange: 'avg',
      timeout: null
    }),
    computed: {
      ...mapState('booking', ['calendarLoading']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('booking', [
        'getRangeTotal',
        'getRangeAverage',
        'getDateRangeInDays',
        'haveClosedDaySelected',
        'havePastDaySelected',
        'haveLowerMinStaySelected',
        'getBookingTrackingV2',
      ]),
      ...mapGetters('hotelData', ['getHotelById']),
      ...mapGetters('currency', ['getDisplayPrice', 'orSelected', 'currencyData']),
      selectedHotel() {
        return this.getHotelById(this.localQuery.hotelCode)
      },
      hotelCurrency() {
        return this.selectedHotel?.currency?.isoCode
      },
      isDollars() {
        return this.currencyData(this.orSelected(this.hotelCurrency))?.Symbol === '$'
      },
      isUSDollars() {
        return this.orSelected(this.hotelCurrency) === 'USD'
      },
      showIsoCode() {
        return (this.isDollars && !this.isUSDollars)
      },
      numberOfNights() {
        return Math.round(this.getDateRangeInDays(this.localQuery))
      },
      priceRanges() {
        return [
          'avg',
          'total'
        ]
      },
      isDisabled() {
        return this.havePastDaySelected(this.localQuery) || this.haveClosedDaySelected(this.localQuery) || this.haveLowerMinStaySelected(this.localQuery) || this.getDateRangeInDays(this.localQuery) === 0
      },
      finalPrice() {
        const finalPrice = this.priceRange === 'avg' ? this.getRangeAverage(this.localQuery) : this.getRangeTotal(this.localQuery)
        return finalPrice ?? 0
      },
      finalPriceLabel() {
        return this.priceRange === 'avg' ? this.tc('booking.avgPerNight') : this.tc('booking.totalStayPreviewPrice')
      },
      havePromoValues() {
        return !!this.localQuery.iataNumber || !!this.localQuery.promotionCode || !!this.localQuery.groupCode
      },
      promoValue() {
        return this.localQuery.iataNumber || this.localQuery.promotionCode || this.localQuery.groupCode
      }
    },
    watch: {
      localQuery: {
        handler(val) {
          this.updateRangeTotal(val)
        },
        deep: true
      },
      calendarLoading: {
        handler(val) {
          if (!val) {
            this.updateRangeTotal(this.localQuery)
          }
        },
      }
    },
    mounted() {
      this.updateRangeTotal(this.localQuery)
    },
    destroyed() {
      clearTimeout(this.timeout)
      this.isLoading = false
    },
    methods: {
      ...mapActions('booking', ['updateRangeTotal']),
      removeEspecialCode() {
        if (!this.calendarLoading) {
          this.localQuery.iataNumber = null
          this.localQuery.promotionCode = null
          this.localQuery.groupCode = null
        }
      },
      submit() {
        this.isLoading = true
        this.$track(this.getBookingTrackingV2(this.localQuery, { event: 'step1' }))
        if (this.localQuery.stayDateStart && this.localQuery.stayDateEnd) {
          this.$track(this.getBookingTrackingV2(this.localQuery, {
            event: 'dateSelected',
            bookingStartDate: this.localQuery.stayDateStart,
            bookingEndDate: this.localQuery.stayDateEnd
          }))
        }
        setTimeout(() => {
          this.runRouteValidation()
        }, 200)
      },
      getRangeAriaLabel(range) {
        const name = this.tc(`booking.${range}`)
        const label = range === 'avg' ? this.tc('booking.avgPerNight') : this.tc('booking.totalStayPreviewPrice')
        const price = range === 'avg' ? this.getRangeAverage(this.localQuery) : this.getRangeTotal(this.localQuery)
        return `${name} ${this.tc('booking.startingAt')} ${price} ${label}`
      },
      bindKeyboardEvent() {
        this.$el.addEventListener('keydown', this.onKeyEvent)
      },
      unbindKeyboardEvent() {
        this.$el.removeEventListener('keydown', this.onKeyEvent)
      },
      onKeyEvent(e) {
        if (e.keyCode === KEY_CODE_MAP.ARROW_RIGHT) {
          this.moveRange(1)
        } else if (e.keyCode === KEY_CODE_MAP.ARROW_LEFT) {
          this.moveRange(-1)
        }
      },
      moveRange(direction = 1) {
        const currentIndex = this.priceRanges.indexOf(this.priceRange)
        const finalIndex = this.priceRanges.length - 1
        let goToButton
        if (direction === -1 && currentIndex === 0) {
          this.priceRange = this.priceRanges[finalIndex]
          goToButton = this.$el.querySelector(`#${this.priceRanges[finalIndex]}-range`)
        } else if (direction === 1 && currentIndex === finalIndex) {
          this.priceRange = this.priceRanges[0]
          goToButton = this.$el.querySelector(`#${this.priceRanges[0]}-range`)
        } else {
          this.priceRange = this.priceRanges[currentIndex + direction]
          goToButton = this.$el.querySelector(`#${this.priceRanges[currentIndex + direction]}-range`)
        }
        this.$nextTick(() => {
          goToButton.focus()
        })
      }
    }
  }
</script>
